// ============================================================================
// App.tsx - Main Application with Routing
// EduPath Africa v2.0 - Routing Architecture
// ============================================================================

import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Auth Components
import {
  AuthProvider,
  ProtectedRoute,
  PublicRoute,
  LearnerRoute,
  GuardianRoute,
  TeacherRoute,
  AdminRoute,
  useAuthInitializer,
} from '@/components/auth';

// Store
import { useAuthStore } from '@/stores/authStore';
import { getDashboardForRole } from '@/lib/roleConfig';

// Error Boundary
import ErrorBoundary from '@/components/shared/ErrorBoundary';

// Layout Shell
import AppLayout from '@/components/layout/AppLayout';

// Sprint 5: Offline Sync Provider
import { OfflineSyncProvider } from '@/components/offline';

// ============================================================================
// Lazy-loaded Pages
// ============================================================================

// Auth Pages
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const VerifyEmailPage = lazy(() => import('@/pages/auth/VerifyEmailPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));

// Public Pages
const LandingPage = lazy(() => import('@/pages/LandingPage'));

// Student Pages
const StudentDashboard = lazy(() => import('@/pages/student/Dashboard'));
const AssessmentPage = lazy(() => import('@/pages/student/AssessmentPage'));
const AssessmentProgressPage = lazy(() => import('@/pages/student/AssessmentProgressPage'));
const AssessmentSummaryPage = lazy(() => import('@/pages/student/AssessmentSummaryPage'));
const AssessmentCompletePage = lazy(() => import('@/pages/student/AssessmentCompletePage'));
const CertificatePage = lazy(() => import('@/pages/student/CertificatePage'));
const AcademicResultsPage = lazy(() => import('@/pages/student/AcademicResultsPage'));
const ReportPage = lazy(() => import('@/pages/student/ReportPage'));
const BasicReportPage = lazy(() => import('@/pages/student/BasicReportPage'));
const PremiumReportPage = lazy(() => import('@/pages/student/PremiumReportPage'));
const GuardianReportPage = lazy(() => import('@/pages/student/GuardianReportPage'));
const SchoolMatchingPage = lazy(() => import('@/pages/student/SchoolMatchingPage'));
const UpgradePage = lazy(() => import('@/pages/student/UpgradePage'));
const ProfilePage = lazy(() => import('@/pages/student/ProfilePage'));

// Guardian Pages
const GuardianDashboard = lazy(() => import('@/pages/guardian/Dashboard'));
const ChildDetailPage = lazy(() => import('@/pages/guardian/ChildDetailPage'));
const LinkChildPage = lazy(() => import('@/pages/guardian/LinkChildPage'));
const ConsentPage = lazy(() => import('@/pages/guardian/ConsentPage'));
const ArisChatPage = lazy(() => import('@/pages/guardian/ArisChatPage'));
const GuardianProfilePage = lazy(() => import('@/pages/guardian/ProfilePage'));

// Shared Pages
const SharedProfilePage = lazy(() => import('@/pages/shared/ProfilePage'));

// Teacher Pages
const TeacherDashboard = lazy(() => import('@/pages/teacher/Dashboard'));
const StudentDetailPage = lazy(() => import('@/pages/teacher/StudentDetailPage'));
const BehavioralRatingPage = lazy(() => import('@/pages/teacher/BehavioralRatingPage'));

// Admin Pages
const AdminDashboard = lazy(() => import('@/pages/admin/Dashboard'));
const SchoolsPage = lazy(() => import('@/pages/admin/SchoolsPage'));
const UsersPage = lazy(() => import('@/pages/admin/UsersPage'));
const LicensesPage = lazy(() => import('@/pages/admin/LicensesPage'));
const EnrollmentsPage = lazy(() => import('@/pages/admin/EnrollmentsPage'));
const AuditLogPage = lazy(() => import('@/pages/admin/AuditLogPage'));
const BulkUsersUploadPage = lazy(() => import('@/pages/admin/BulkUsersUploadPage'));
const BulkPerformanceUploadPage = lazy(() => import('@/pages/admin/BulkPerformanceUploadPage'));

// Sprint 3: Year Lifecycle & Promotion Pages
const AdminYearManagerPage = lazy(() => import('@/pages/admin/AdminYearManagerPage'));
const AdminPromotionConsolePage = lazy(() => import('@/pages/admin/AdminPromotionConsolePage'));

// School Admin Pages
const SchoolDashboard = lazy(() => import('@/pages/school/Dashboard'));
const SchoolEnrollmentsPage = lazy(() => import('@/pages/school/EnrollmentsPage'));
const SchoolReportsPage = lazy(() => import('@/pages/school/ReportsPage'));
const SchoolTeachersPage = lazy(() => import('@/pages/school/TeachersPage'));
const SchoolSettingsPage = lazy(() => import('@/pages/school/SettingsPage'));
const BulkEnrollmentUpload = lazy(() => import('@/components/admin/BulkEnrollmentUpload'));
const StudentPinManagement = lazy(() => import('@/components/admin/StudentPinManagement'));

// Error Pages
const NotFoundPage = lazy(() => import('@/pages/errors/NotFoundPage'));

// Profile Completion Guard - blocks assessments for incomplete profiles
const ProfileCompletionGuard = lazy(() => import('@/components/auth/ProfileCompletionGuard'));

// Legal Pages
const TermsPage = lazy(() => import('@/pages/legal/TermsPage'));
const PrivacyPage = lazy(() => import('@/pages/legal/PrivacyPage'));

// ============================================================================
// Query Client Configuration
// ============================================================================

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// ============================================================================
// Loading Fallback
// ============================================================================

const PageLoadingFallback: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-600 to-primary-700">
    <div className="text-center">
      <img src="/edupath-logo.png" alt="EduPath Africa" className="h-16 mx-auto mb-6 animate-pulse" />
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white mx-auto"></div>
      <p className="mt-4 text-primary-100 text-sm">Loading...</p>
    </div>
  </div>
);

// ============================================================================
// Root Redirect Component
// ============================================================================

/**
 * Handles the root "/" redirect based on auth state.
 * Shows LandingPage for unauthenticated users, redirects to dashboard for authenticated.
 */
const RootRedirect: React.FC = () => {
  const { isAuthenticated, user, isHydrated } = useAuthStore();

  // Wait for hydration - this is handled by AuthProvider, but double-check
  if (!isHydrated) {
    return <PageLoadingFallback />;
  }

  // Redirect authenticated users to their dashboard
  if (isAuthenticated && user) {
    const dashboard = getDashboardForRole(user.role);
    return <Navigate to={dashboard} replace />;
  }

  // Show landing page for unauthenticated users
  return <LandingPage />;
};

// ============================================================================
// App Routes Component
// ============================================================================

const AppRoutes: React.FC = () => {
  // Initialize auth-related side effects
  useAuthInitializer();

  return (
    <Routes>
      {/* ================================================================ */}
      {/* Public Routes (Auth Pages) */}
      {/* ================================================================ */}

      {/* Login - Redirects authenticated users to dashboard */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Register - Redirects authenticated users to dashboard */}
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />

      {/* Password Reset Flow - No redirect for authenticated users */}
      <Route
        path="/forgot-password"
        element={
          <PublicRoute redirectAuthenticated={false}>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />

      <Route
        path="/reset-password"
        element={
          <PublicRoute redirectAuthenticated={false}>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />

      {/* Email Verification */}
      <Route
        path="/verify-email"
        element={
          <PublicRoute redirectAuthenticated={false}>
            <VerifyEmailPage />
          </PublicRoute>
        }
      />

      {/* Legal Pages - Public */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />

      {/* ================================================================ */}
      {/* Authenticated Routes - Wrapped in Corporate Layout */}
      {/* ================================================================ */}
      <Route element={<AppLayout />}>
        {/* ============================================================ */}
        {/* Student Routes (LEARNER role) */}
        {/* ============================================================ */}
        <Route path="/student" element={<LearnerRoute />}>
          <Route path="dashboard" element={<StudentDashboard />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="schools" element={<SchoolMatchingPage />} />
          <Route path="upgrade" element={<UpgradePage />} />
          <Route path="report" element={<BasicReportPage />} />
          <Route path="report/basic" element={<BasicReportPage />} />
          <Route path="report/premium" element={<PremiumReportPage />} />
          <Route path="report/guardian" element={<GuardianReportPage />} />
          <Route path="assessment-summary" element={<AssessmentSummaryPage />} />
          <Route path="assessment-complete" element={<AssessmentCompletePage />} />
          <Route path="certificate" element={<CertificatePage />} />
          {/* Convenience routes - redirect to correct paths */}
          <Route path="assessments" element={<Navigate to="/assessments/progress" replace />} />
          <Route path="progress" element={<Navigate to="/assessments/progress" replace />} />
          <Route index element={<Navigate to="dashboard" replace />} />
        </Route>

        {/* Assessment Routes - Profile gate blocks incomplete profiles */}
        <Route path="/assessments" element={<LearnerRoute />}>
          {/* Progress page accessible without profile gate (shows status) */}
          <Route path="progress" element={<AssessmentProgressPage />} />
          <Route path="academic" element={<AcademicResultsPage />} />
          {/* Assessment-taking routes guarded by profile completion */}
          <Route element={<ProfileCompletionGuard />}>
            <Route path="summary" element={<AssessmentSummaryPage />} />
            <Route path="complete" element={<AssessmentCompletePage />} />
            {/* Wildcard route LAST - catches interest, personality, numerical, verbal, spatial */}
            <Route path=":type" element={<AssessmentPage />} />
          </Route>
          <Route index element={<Navigate to="progress" replace />} />
        </Route>

        {/* Report Routes */}
        <Route path="/reports" element={<LearnerRoute />}>
          <Route path="basic" element={<BasicReportPage />} />
          <Route path="premium" element={<PremiumReportPage />} />
          <Route path="guardian" element={<GuardianReportPage />} />
          <Route path=":learnerId" element={<ReportPage />} />
          <Route index element={<BasicReportPage />} />
        </Route>

        {/* Other Student Routes */}
        <Route
          path="/schools/eligible"
          element={
            <LearnerRoute>
              <SchoolMatchingPage />
            </LearnerRoute>
          }
        />

        <Route
          path="/upgrade"
          element={
            <LearnerRoute>
              <UpgradePage />
            </LearnerRoute>
          }
        />

        {/* ============================================================ */}
        {/* Guardian Routes */}
        {/* ============================================================ */}
        <Route path="/guardian" element={<GuardianRoute />}>
          <Route path="dashboard" element={<GuardianDashboard />} />
          <Route path="child/:childId" element={<ChildDetailPage />} />
          <Route path="link" element={<LinkChildPage />} />
          <Route path="consent" element={<ConsentPage />} />
          <Route path="profile" element={<GuardianProfilePage />} />
          <Route index element={<Navigate to="dashboard" replace />} />
        </Route>

        <Route
          path="/aris"
          element={
            <GuardianRoute>
              <ArisChatPage />
            </GuardianRoute>
          }
        />

        {/* ============================================================ */}
        {/* Teacher/Counselor Routes */}
        {/* ============================================================ */}
        <Route path="/teacher" element={<TeacherRoute />}>
          <Route path="dashboard" element={<TeacherDashboard />} />
          <Route path="student/:studentId" element={<StudentDetailPage />} />
          <Route path="rating/:studentId" element={<BehavioralRatingPage />} />
          <Route path="profile" element={<SharedProfilePage />} />
          <Route index element={<Navigate to="dashboard" replace />} />
        </Route>

        {/* Counselor Routes - Per Spec Section 2.3 */}
        <Route path="/counselor" element={<ProtectedRoute allowedRoles={['COUNSELOR', 'ADMIN', 'SUPER_ADMIN']} />}>
          <Route path="dashboard" element={<TeacherDashboard />} />
          <Route path="escalations" element={<TeacherDashboard />} />
          <Route path="student/:studentId" element={<StudentDetailPage />} />
          <Route index element={<Navigate to="dashboard" replace />} />
        </Route>

        {/* ============================================================ */}
        {/* School Admin Routes */}
        {/* ============================================================ */}
        <Route
          path="/school"
          element={<ProtectedRoute allowedRoles={['SCHOOL_ADMIN', 'ADMIN', 'SUPER_ADMIN']} />}
        >
          <Route path="dashboard" element={<SchoolDashboard />} />
          <Route path="enrollments" element={<SchoolEnrollmentsPage />} />
          <Route path="reports" element={<SchoolReportsPage />} />
          <Route path="teachers" element={<SchoolTeachersPage />} />
          <Route path="settings" element={<SchoolSettingsPage />} />
          <Route path="bulk-enrollment" element={<BulkEnrollmentUpload />} />
          <Route path="pin-management" element={<StudentPinManagement />} />
          <Route index element={<Navigate to="dashboard" replace />} />
        </Route>

        {/* ============================================================ */}
        {/* Platform Admin Routes */}
        {/* ============================================================ */}
        <Route path="/admin" element={<AdminRoute />}>
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="schools" element={<SchoolsPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="licenses" element={<LicensesPage />} />
          <Route path="enrollments" element={<EnrollmentsPage />} />
          <Route path="audit" element={<AuditLogPage />} />
          <Route path="bulk-users" element={<BulkUsersUploadPage />} />
          <Route path="bulk-performance" element={<BulkPerformanceUploadPage />} />
          <Route path="academic-years" element={<AdminYearManagerPage />} />
          <Route path="promotions" element={<AdminPromotionConsolePage />} />
          <Route path="profile" element={<SharedProfilePage />} />
          <Route index element={<Navigate to="dashboard" replace />} />
        </Route>
      </Route>

      {/* ================================================================ */}
      {/* Root Redirect */}
      {/* ================================================================ */}
      <Route path="/" element={<RootRedirect />} />

      {/* ================================================================ */}
      {/* 404 - Catch All */}
      {/* ================================================================ */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

// ============================================================================
// Main App Component
// ============================================================================

export const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ErrorBoundary>
            <Suspense fallback={<PageLoadingFallback />}>
              <OfflineSyncProvider>
                <AppRoutes />
              </OfflineSyncProvider>
            </Suspense>
          </ErrorBoundary>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
